import React, { useState, useEffect, useRef, useCallback } from "react";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import { Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import {
  getAllCustomers,
  assignToGroup,
} from "../../../redux/group/groupSlice";

const DEBOUNCE_DELAY = 700;

const AddCustomer = ({
  onOpen,
  onClose,
  onSave,
  groupId,
  groupName,
  groupOffsetId,
}) => {
  const dispatch = useDispatch();
  const { addCustomers, loading } = useSelector((state) => state.group);

  const [validTill, setValidTill] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const debounceTimer = useRef(null);
  const [errors, setErrors] = useState({
    customer: "",
    validTill: "",
  });
  // Fetch initial 20 customers when modal opens
  useEffect(() => {
    if (onOpen) {
      dispatch(getAllCustomers({ pageNo: 1, limit: 20, search: "" }));
    }
  }, [onOpen, dispatch]);

  // Debounced search handler
  const handleSearchChange = useCallback(
    (value) => {
      setSearchInput(value);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(() => {
        dispatch(getAllCustomers({ pageNo: 1, limit: 20, search: value }));
      }, DEBOUNCE_DELAY);
    },
    [dispatch],
  );

  const handleSave = async () => {
    const newErrors = {
      customer: "",
      validTill: "",
    };

    if (!selectedCustomer) {
      newErrors.customer = "Customer is required";
    }

    if (!validTill) {
      newErrors.validTill = "Valid till date is required";
    }

    setErrors(newErrors);

    if (newErrors.customer || newErrors.validTill) return;

    const payload = {
      groupId,
      groupName,
      groupOffsetId,
      clientId: selectedCustomer._id,
      validTill,
    };

    try {
      await dispatch(assignToGroup(payload)).unwrap();
      handleCancel();
      onSave();
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        customer: err || "Customer already exists",
      }));
    }
  };
  const handleCancel = () => {
    setSelectedCustomer(null);
    setSearchInput("");
    setValidTill("");
    setErrorMessage("");
    onClose();
  };

  return (
    <DailogBox
      open={onOpen}
      onCancel={handleCancel}
      width="90%"
      maxWidth="600px"
    >
      <h3 className="text-xl font-semibold">Add Customer</h3>
      <div className="p-2 mt-4">
        <hr />
      </div>

      <div className="flex flex-col w-full mt-6 gap-5">
        {/* Customer Select with Debounced Search */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1">Select Customer</label>
          <Select
            showSearch
            allowClear
            placeholder="Search customer..."
            className="h-14"
            value={selectedCustomer?._id}
            filterOption={false} // search handled via API
            loading={loading}
            onSearch={handleSearchChange}
            onChange={(value) => {
              const selected = (addCustomers || []).find(
                (c) => c._id === value,
              );

              setSelectedCustomer(selected || null);

              setErrors((prev) => ({ ...prev, customer: "" }));
            }}
            notFoundContent={loading ? "Searching..." : "No customers found"}
          >
            {(addCustomers || []).map((item) => (
              <Select.Option key={item._id} value={item._id}>
                {item.organization?.name || item.name}
              </Select.Option>
            ))}
          </Select>
          {errors.customer && (
            <span className="text-red-500 text-sm mt-1">{errors.customer}</span>
          )}
        </div>

        {/* Valid Till Date */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1">Valid Till</label>
          <input
            type="date"
            value={validTill}
            onChange={(e) => {
              setValidTill(e.target.value);
              setErrors((prev) => ({ ...prev, validTill: "" }));
            }}
            min={new Date().toISOString().split("T")[0]}
            className="h-14 px-4 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#06513D] focus:border-[#06513D]"
          />
          {errors.validTill && (
            <span className="text-red-500 text-sm mt-1">
              {errors.validTill}
            </span>
          )}
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="text-red-500 text-sm">{errorMessage}</div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-center gap-4 py-8">
        <CustomButton
          buttonType="primary"
          variant="cancel"
          buttonText="Cancel"
          className="h-[50px] w-[200px]"
          onClick={handleCancel}
        />
        <CustomButton
          buttonType="primary"
          variant="ok"
          buttonText="Save"
          className="h-[50px] w-[200px]"
          onClick={handleSave}
        />
      </div>
    </DailogBox>
  );
};

export default AddCustomer;
